import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#ensureFreshContent);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#ensureFreshContent);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /**
   * Self-heals the drawer when it is opened with stale markup.
   *
   * The cart drawer lives inside the header section. Ajax adds used to morph
   * that whole section, which on the live storefront could leave a filled
   * drawer with frozen quantity controls. This refetches just the drawer's
   * inner HTML when the empty state disagrees with the bag icon, or when the
   * heading count lags behind the icon.
   */
  #ensureFreshContent = async () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const inner = dialog?.querySelector('[data-hydration-key="cart-drawer-inner"]');
    if (!inner) return;

    // A filled drawer always contains the cart form; the empty state does not.
    const showsEmpty = !inner.querySelector('.cart-form');
    const headerCount = parseInt(
      document.querySelector('cart-icon .cart-bubble__text-count')?.textContent || '0',
      10
    ) || 0;
    const drawerCount = parseInt(
      inner.querySelector('.cart-drawer__heading .cart-bubble__text-count')?.textContent || '0',
      10
    ) || 0;
    const hasItems = document.querySelector('cart-icon.header-actions__cart-icon--has-cart') != null;

    // Heal empty-but-has-items, and also a filled drawer whose line count
    // lagged behind the bag icon (the live Ajax-add freeze).
    const needsRefresh = (showsEmpty && hasItems) || (!showsEmpty && headerCount > 0 && headerCount !== drawerCount);
    if (!needsRefresh) return;

    const sectionId = /** @type {HTMLElement | null} */ (
      inner.querySelector('cart-items-component[data-section-id]')
    )?.dataset.sectionId;
    if (!sectionId) return;

    try {
      if (typeof window.brRefreshCartDrawer === 'function') {
        await window.brRefreshCartDrawer();
        this.#updateStickyState();
        return;
      }

      const html = await sectionRenderer.getSectionHTML(sectionId, false);
      const freshInner = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-hydration-key="cart-drawer-inner"]');

      // If the freshly rendered drawer is also empty, the cart really is empty.
      if (!freshInner || !freshInner.querySelector('.cart-form')) return;

      inner.innerHTML = freshInner.innerHTML;
      dialog?.classList.remove('cart-drawer--empty');
      this.#updateStickyState();
    } catch {
      // Non-fatal: leave the existing content in place on any failure.
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
